// Estrutura para o versículo do dia
export type DailyVerse = {
  id: number;
  verse: string;
  ref: string;
};

export type DailyVerses = {
  [key: string]: DailyVerse[];
};

// Importa CATEGORIES para validação
import { CATEGORIES } from './playlists';

// Mapeamento do versículo do dia por dia da semana
export const DAILY_VERSES: DailyVerses = {
  monday: [
    {
      id: 144,
      verse: '"O senhor é meu pastor e nada me faltará"',
      ref: 'Salmo 23:1-6'
    }
  ],
  tuesday: [
    {
      id: 3,
      verse: '"Buscai primeiro o Reino de Deus"',
      ref: 'Mateus 6:33-34'
    }
  ],
  wednesday: [
    {
      id: 318,
      verse: '"Tomai o escudo da fé... e a espada do Espírito, que é a palavra de Deus."',
      ref: 'Efésios 6:16-24'
    }
  ],
  thursday: [
    {
      id: 320,
      verse: '"Vinde a mim todos os que estais cansados"',
      ref: 'Mateus 11:26-30'
    }
  ],
  friday: [
    {
      id: 419,
      verse: '"Tu conservarás em paz aquele cuja mente está firme em Ti."',
      ref: 'Isaías 26:3-4'
    }
  ],
  saturday: [
    {
      id: 341,
      verse: '"Eu sou o pão da vida"',
      ref: 'João 6:35-40'
    }
  ],
  sunday: [
    {
      id: 145,
      verse: '"Aquele que habita no esconderijo do Altíssimo, à sombra do Onipotente descansará."',
      ref: 'Salmo 91:1-16'
    }
  ]
};

// Mapeamento de cada música do versículo diário para sua categoria correta
export const DAILY_VERSE_CATEGORY_MAP: Record<number, string> = {
  // Segunda-feira
  144: 'esperanca',  // Salmo 23 - está em esperança
  
  // Terça-feira
  3: 'fe',           // Mateus 6:33 - está em fé
  
  // Quarta-feira
  318: 'fe',         // Efésios 6:16 - está em fé
  
  // Quinta-feira
  320: 'esperanca',  // Mateus 11:26 - está em esperança
  
  // Sexta-feira
  419: 'fe',         // Isaías 26:3 - está em fé
  
  // Sábado
  341: 'esperanca',  // João 6:35 - está em esperança
  
  // Domingo
  145: 'fe'          // Salmo 91 - está em fé
};

// Função para obter a categoria correta de uma música do versículo diário
export const getDailyVerseSongCategory = (songId: number): string | null => {
  return DAILY_VERSE_CATEGORY_MAP[songId] || null;
};

// Função para verificar se uma música pertence ao versículo diário
export const isDailyVerseSong = (songId: number): boolean => {
  return songId in DAILY_VERSE_CATEGORY_MAP;
};

// Função para obter todos os IDs únicos do versículo diário
export const getAllDailyVerseSongIds = (): number[] => {
  return Object.keys(DAILY_VERSE_CATEGORY_MAP).map(Number);
};

// 🔍 FUNÇÃO DE DIAGNÓSTICO: Valida integridade do mapeamento
export const validateDailyVersesMapping = () => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const categoriesUsed = new Set<string>();
  const allDailyVerseIds = new Set<number>();
  const duplicatedSongs = new Set<number>();
  const unmappedSongs = new Set<number>();

  // Validação básica
  Object.entries(DAILY_VERSES).forEach(([date, dayVerses]) => {
    dayVerses.forEach(verse => {
      if (typeof verse.id === 'number') {
        allDailyVerseIds.add(verse.id);
      }
    });
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    stats: {
      totalSongs: allDailyVerseIds.size,
      categoriesUsed: Array.from(categoriesUsed),
      duplicatedSongs: Array.from(duplicatedSongs),
      unmappedSongs: Array.from(unmappedSongs)
    }
  };
};

// 🔍 FUNÇÃO DE DIAGNÓSTICO: Verifica se uma música está na categoria correta
export const validateSongInCategory = (songId: number): {
  isValid: boolean;
  mappedCategory: string | null;
  actualCategories: string[];
  recommendation: string;
} => {
  const mappedCategory = getDailyVerseSongCategory(songId);
  const actualCategories: string[] = [];
  
  // Busca em todas as categorias
  Object.entries(CATEGORIES).forEach(([categoryKey, categoryData]) => {
    if (categoryData.songIds && categoryData.songIds.includes(songId)) {
      actualCategories.push(categoryKey);
    }
  });
  
  let recommendation = '';
  let isValid = false;
  
  if (!mappedCategory && actualCategories.length === 0) {
    recommendation = `Música ${songId} não encontrada em nenhuma categoria. Será carregada individualmente.`;
    isValid = true; // Não é erro, apenas fallback
  } else if (!mappedCategory && actualCategories.length > 0) {
    recommendation = `Música ${songId} deveria ser mapeada para: ${actualCategories[0]}`;
    isValid = false;
  } else if (mappedCategory && !actualCategories.includes(mappedCategory)) {
    recommendation = `Música ${songId} mapeada para '${mappedCategory}' mas está em: ${actualCategories.join(', ')}`;
    isValid = false;
  } else if (mappedCategory && actualCategories.includes(mappedCategory)) {
    recommendation = `Música ${songId} corretamente mapeada para '${mappedCategory}'`;
    isValid = true;
  }
  
  return {
    isValid,
    mappedCategory,
    actualCategories,
    recommendation
  };
};

// 🔍 FUNÇÃO DE DIAGNÓSTICO: Gera relatório completo
export const generateDailyVersesReport = (): string => {
  const validation = validateDailyVersesMapping();
  const lines: string[] = [];
  
  lines.push('=== RELATÓRIO DE VERSÍCULO DIÁRIO ===\n');
  
  lines.push(`📊 ESTATÍSTICAS:`);
  lines.push(`- Total de músicas: ${validation.stats.totalSongs}`);
  lines.push(`- Categorias utilizadas: ${validation.stats.categoriesUsed.join(', ')}`);
  lines.push(`- Músicas duplicadas: ${validation.stats.duplicatedSongs.length}`);
  lines.push(`- Músicas não mapeadas: ${validation.stats.unmappedSongs.length}\n`);
  
  if (validation.errors.length > 0) {
    lines.push(`❌ ERROS (${validation.errors.length}):`);
    validation.errors.forEach(error => lines.push(`- ${error}`));
    lines.push('');
  }
  
  if (validation.warnings.length > 0) {
    lines.push(`⚠️ AVISOS (${validation.warnings.length}):`);
    validation.warnings.forEach(warning => lines.push(`- ${warning}`));
    lines.push('');
  }
  
  lines.push(`🔍 VALIDAÇÃO POR MÚSICA:`);
  getAllDailyVerseSongIds().forEach(songId => {
    const songValidation = validateSongInCategory(songId);
    const status = songValidation.isValid ? '✅' : '❌';
    lines.push(`${status} ${songValidation.recommendation}`);
  });
  
  lines.push(`\n📋 RESUMO: ${validation.isValid ? 'VÁLIDO' : 'REQUER CORREÇÕES'}`);
  
  return lines.join('\n');
};

// Função para obter o versículo do dia atual
export const getDailyVerses = (): DailyVerse[] => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const today = days[new Date().getDay()];
  return DAILY_VERSES[today];
}; 